/**
 * The nav badge's read: a SMALL projection that answers "how many repair
 * orders are in the Workboard's Needs Attention lane?" with the board's own
 * logical-owner resolution and lane rule — not the heavy Workboard fetch.
 *
 * Needs Attention can only come from (a) a repair order the front office
 * holds or must accept (an FOH custody row), or (b) an advisor-owned open
 * estimate. So the projection reads exactly those, plus whatever the lane
 * rule needs to judge them as whole repair orders:
 *
 *   1  FOH custody rows + their tickets' lane facts      (1 query)
 *   2  advisor-owned open estimates                      (1 query)
 *   3  the other segments of those repair orders (rollover A ← B ← C), so a
 *      chain whose custody is split across segments is ONE repair order
 *                                                       (1 query per 100 owners)
 *   4  owners' commercial states                         (1 query per 150)
 *   5  open pauses of held segments                      (0–1 query)
 *
 * ≈ 4–5 round trips whatever the shop size; no per-ticket reads. Then
 * buildLogicalRepairOrders + summarizeFohAttention — the same functions the
 * Workboard renders with — so the badge cannot count a repair order twice or
 * count one the board would not show.
 */
import { supabase } from '@/integrations/supabase/client';
import { FOH_QUEUE_STATUSES } from '@/components/lifecycle/FohCustodyQueue';
import {
  buildLogicalRepairOrders,
  FOH_LANE_TICKET_COLUMNS,
  isTombstoneSegment,
  logicalOwnerId,
  summarizeFohAttention,
  type FohAttentionSummary,
  type WorkboardAssignment,
  type WorkboardBusinessState,
  type WorkboardCustody,
  type WorkboardPause,
  type WorkboardTicket,
} from '@/lib/foh/workboard/workboardModel';

type CustodyWithTicket = WorkboardCustody & { ticket: WorkboardTicket | null };

const quote = (ids: readonly string[]) => ids.map((id) => `"${id.replace(/"/g, '')}"`).join(',');
function chunk<T>(xs: readonly T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}
async function each<R>(lists: string[][], read: (ids: string[]) => PromiseLike<{ data: unknown; error: unknown }>): Promise<R[]> {
  const res = await Promise.all(lists.map((ids) => read(ids)));
  const out: R[] = [];
  for (const r of res) {
    if (r.error) throw r.error;
    out.push(...((r.data ?? []) as R[]));
  }
  return out;
}

export async function fetchFohAttentionSummary(shopId: string): Promise<FohAttentionSummary> {
  const sb = supabase as any;
  const [custodyRes, estimatesRes] = await Promise.all([
    sb.from('ticket_custody')
      .select(`ticket_id, custody_owner, custody_status, holder_user_id, entered_at, ticket:tickets!ticket_id ( ${FOH_LANE_TICKET_COLUMNS} )`)
      .eq('shop_id', shopId).in('custody_status', FOH_QUEUE_STATUSES as unknown as string[]),
    sb.from('ticket_advisor_assignments')
      .select(`ticket_id, advisor_user_id, ticket:tickets!inner ( ${FOH_LANE_TICKET_COLUMNS} )`)
      .eq('shop_id', shopId).not('advisor_user_id', 'is', null)
      .eq('ticket.is_estimate', true).is('ticket.deleted_at', null).is('ticket.picked_up_at', null),
  ]);
  if (custodyRes.error) throw custodyRes.error;
  if (estimatesRes.error) throw estimatesRes.error;
  const custodyRows = (custodyRes.data ?? []) as CustodyWithTicket[];
  const estimateRows = (estimatesRes.data ?? []) as Array<WorkboardAssignment & { ticket: WorkboardTicket | null }>;

  const tickets = new Map<string, WorkboardTicket>();
  for (const r of [...custodyRows, ...estimateRows]) if (r.ticket) tickets.set(r.ticket.id, r.ticket);

  // Whole repair orders: pull every segment of the chains these rows belong to.
  const owners = [...new Set(custodyRows.flatMap((r) => (r.ticket ? [logicalOwnerId(r.ticket, tickets)] : [])))];
  const members = await each<WorkboardTicket>(chunk(owners, 100), (ids) =>
    sb.from('tickets').select(FOH_LANE_TICKET_COLUMNS)
      .or(`id.in.(${quote(ids)}),parent_ticket_id.in.(${quote(ids)}),rollover_of_ticket_id.in.(${quote(ids)})`));
  for (const t of members) tickets.set(t.id, t);

  const all = [...tickets.values()];
  const ownerIds = [...new Set(all.map((t) => logicalOwnerId(t, tickets)))];
  const heldIds = all.filter((t) => !isTombstoneSegment(t) && (t.status === 'Paused' || t.paused_at != null)).map((t) => t.id);
  const [businessStates, pauses] = await Promise.all([
    each<WorkboardBusinessState>(chunk(ownerIds, 150), (ids) =>
      sb.from('ticket_business_state').select('ticket_id, current_state, entered_at').in('ticket_id', ids)),
    each<WorkboardPause>(chunk(heldIds, 150), (ids) =>
      sb.from('ticket_pause_sessions').select('ticket_id, pause_intent, pause_reason')
        .in('ticket_id', ids).is('resumed_at', null).is('resolved_at', null)),
  ]);

  const ros = buildLogicalRepairOrders({
    tickets: all,
    custody: custodyRows.map(({ ticket: _t, ...c }) => c),
    assignments: estimateRows.map((e) => ({ ticket_id: e.ticket_id, advisor_user_id: e.advisor_user_id })),
    businessStates,
    pauses,
  });
  // Needs Attention never depends on the shop day (only Done Today / Upcoming
  // do), so the badge needs no timezone.
  return summarizeFohAttention(ros, { today: '', shopTimezone: 'UTC' });
}
